import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  addLead,
  updateLead,
  getLeadById,
  getLeadStatuses,
} from "../../services/leadService";

import { getCategories } from "../../services/categoryService";

import "../users/AddUserLayer.css";

const AddLeadLayer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [statuses, setStatuses] = useState([]);
  const [categories, setCategories] = useState([]);

  const [form, setForm] = useState({
    name: "",
    category_id: "",
    phone: "",
    email: "",
    source: "",
    discussion: "",
    remark: "",
    followup_date: "",
    status: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  /* =========================
     LOAD DATA
  ========================= */
  useEffect(() => {
    const init = async () => {
      await loadStatuses();
      await loadCategories();

      if (isEdit) {
        await loadLead();
      }
    };

    init();
  }, [id]);

  /* =========================
     LOAD STATUSES
  ========================= */
  const loadStatuses = async () => {
    const res = await getLeadStatuses();

    if (res.status === "success") {
      setStatuses(res.data || []);
    }
  };

  /* =========================
     LOAD CATEGORIES
  ========================= */
  const loadCategories = async () => {
    const res = await getCategories();

    if (res.status === "success") {
      setCategories(res.data || []);
    }
  };

  /* =========================
     LOAD LEAD FOR EDIT
  ========================= */
  const loadLead = async () => {
    const res = await getLeadById(id);

    if (res.status === "success" && res.data) {
      const lead = res.data;

      setForm({
        name: lead.name || "",
        category_id: lead.category_id || "",
        phone: lead.phone || "",
        email: lead.email || "",
        source: lead.source || "",
        discussion: lead.discussion || "",
        remark: lead.remark || "",
        followup_date: lead.followup_date
          ? lead.followup_date.split(" ")[0]
          : "",
        status: lead.status || "",
      });
    }
  };

  /* =========================
     HANDLE CHANGE
  ========================= */
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });

    setErrors({ ...errors, [e.target.name]: "" });
  };

  /* =========================
     VALIDATION
  ========================= */
  const validate = () => {
    let err = {};

    if (!form.name.trim()) {
      err.name = "Lead name is required";
    }

    if (!form.category_id) {
      err.category_id = "Category is required";
    }

    if (!form.phone) {
      err.phone = "Mobile number is required";
    } else if (!/^[6-9]\d{9}$/.test(form.phone)) {
      err.phone = "Invalid mobile number";
    }

    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      err.email = "Invalid email address";
    }

    if (!form.status) {
      err.status = "Lead status required";
    }

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  /* =========================
     SUBMIT
  ========================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);

    const res = isEdit
      ? await updateLead({ id, ...form })
      : await addLead(form);

    setLoading(false);

    if (res.status === "success") {
      toast.success(
        isEdit ? "Lead updated successfully" : "Lead added successfully"
      );

      navigate("/lead-list");
    } else {
      toast.error(res.message || "Action failed");
    }
  };

  return (
    <div className="add-user-wrapper">
      <div className="add-user-card">

        <h5>{isEdit ? "Update Lead" : "Add Lead"}</h5>

        <form onSubmit={handleSubmit}>

          <div className="grid">

            {/* NAME */}
            <div className="form-group">
              <label>Lead Name *</label>

              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Enter lead name"
              />

              {errors.name && (
                <span className="error">{errors.name}</span>
              )}
            </div>

            {/* CATEGORY */}
            <div className="form-group">
              <label>Category *</label>

              <select
                name="category_id"
                value={form.category_id}
                onChange={handleChange}
              >
                <option value="">Select category</option>

                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}

              </select>

              {errors.category_id && (
                <span className="error">{errors.category_id}</span>
              )}
            </div>

            {/* MOBILE */}
            <div className="form-group">
              <label>Mobile *</label>

              <input
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="Enter mobile number"
              />

              {errors.phone && (
                <span className="error">{errors.phone}</span>
              )}
            </div>

            {/* EMAIL */}
            <div className="form-group">
              <label>Email</label>

              <input
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="Enter email address"
              />

              {errors.email && (
                <span className="error">{errors.email}</span>
              )}
            </div>

            {/* SOURCE */}
            <div className="form-group">
              <label>Source</label>

              <select
                name="source"
                value={form.source}
                onChange={handleChange}
              >
                <option value="">Select source</option>
                <option value="Website">Website</option>
                <option value="Facebook">Facebook</option>
                <option value="Google Ads">Google Ads</option>
                <option value="Reference">Reference</option>
              </select>
            </div>

            {/* STATUS */}
            <div className="form-group">
              <label>Status *</label>

              <select
                name="status"
                value={form.status}
                onChange={handleChange}
              >
                <option value="">Select status</option>

                {statuses.map((s) => (
                  <option key={s.id} value={s.name}>
                    {s.name}
                  </option>
                ))}

              </select>

              {errors.status && (
                <span className="error">{errors.status}</span>
              )}
            </div>

            {/* FOLLOWUP */}
            <div className="form-group">
              <label>Follow Up Date</label>

              <input
                type="date"
                name="followup_date"
                value={form.followup_date}
                onChange={handleChange}
              />
            </div>

            {/* DISCUSSION */}
            <div className="form-group full-width">
              <label>Discussion</label>

              <textarea
                name="discussion"
                value={form.discussion}
                onChange={handleChange}
                placeholder="Enter discussion notes"
              />
            </div>

            {/* REMARK */}
            <div className="form-group full-width">
              <label>Remark</label>

              <textarea
                name="remark"
                value={form.remark}
                onChange={handleChange}
                placeholder="Enter remark"
              />
            </div>

          </div>

          <div className="form-actions">
            <button className="btn-primary" disabled={loading}>
              {loading
                ? "Saving..."
                : isEdit
                ? "Update Lead"
                : "Save Lead"}
            </button>
          </div>

        </form>

      </div>
    </div>
  );
};

export default AddLeadLayer;